import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./styles.scss";

import Nav from "./Nav";
import Footer from "./Footer";
import Hero from "./Hero";
import AgentAnatomy from "./AgentAnatomy";
import DemoTheater from "./DemoTheater";
import IndustriesSection from "./IndustriesSection";
import FunctionsSection from "./FunctionsSection";
import ProcessSection, { RoiSection } from "./ProcessSection";
import ComparisonSection from "./ComparisonSection";
import PricingSection from "./PricingSection";
import TestimonialsSection from "./TestimonialsSection";
import { SecuritySection, FaqSection } from "./SecurityFaq";
import FinalCta from "./FinalCta";

/**
 * Smooth-scroll to in-page anchors when the URL hash changes
 * (so /#pricing, /#demo, /#book all work after route navigation).
 */
function useHashScroll() {
  const loc = useLocation();
  useEffect(() => {
    if (!loc.hash) return;
    const id = loc.hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      // Small delay so the section is mounted before we scroll
      requestAnimationFrame(() => {
        window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 64, behavior: "smooth" });
      });
    }
  }, [loc.hash, loc.pathname]);
}

export default function HomePage() {
  useHashScroll();
  return (
    <div className="ai-root min-h-screen" data-screen-label="EkkBaz AI · Home">
      <Helmet>
        <title>EkkBaz AI — Custom Self-Learning AI Agents for Business Automation</title>
        <meta name="description" content="EkkBaz AI builds custom, self-learning AI agents that automate procurement, sales, supply chain, customer experience and finance — replacing manual work and cutting operational costs by 25–35%. First agent live in 6 weeks." />
        <link rel="canonical" href="https://ekkbaz.ai/" />
        <meta property="og:title" content="EkkBaz AI — Custom Self-Learning AI Agents for Business Automation" />
        <meta property="og:description" content="Self-learning AI agents that take over operational work. Cut costs 25–35%. First agent live in 6 weeks." />
        <meta property="og:url" content="https://ekkbaz.ai/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://ekkbaz.ai/assets/images/ekkbaz-og.png" />
        <meta name="twitter:title" content="EkkBaz AI — Custom Self-Learning AI Agents" />
        <meta name="twitter:description" content="Self-learning agents that take over operational work. Cut costs 25–35%. Live in 6 weeks." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": "https://ekkbaz.ai/#webpage",
          "url": "https://ekkbaz.ai/",
          "name": "EkkBaz AI — Custom Self-Learning AI Agents for Business Automation",
          "isPartOf": {"@id": "https://ekkbaz.ai/#website"},
          "about": {"@id": "https://ekkbaz.ai/#organization"},
          "description": "EkkBaz AI builds custom, self-learning AI agents that automate procurement, sales, supply chain, customer experience and finance for B2B enterprises.",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": ["h1", "h2", ".hero-headline", ".section-headline"]
          }
        })}</script>
      </Helmet>
      <Nav />
      <main>
        <Hero />
        <AgentAnatomy />
        <DemoTheater />
        <IndustriesSection />
        <FunctionsSection />
        <ProcessSection />
        <RoiSection />
        <ComparisonSection />
        <PricingSection />
        <TestimonialsSection />
        <SecuritySection />
        <FaqSection />
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
}
